import { useState } from "react";
import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  Youtube,
  Globe,
  ExternalLink,
} from "lucide-react";
import "./footer.css";

const Footer = () => {
  const [currentYear] = useState(new Date().getFullYear());

  return (
    <footer className="bg-gradient-to-br from-[#1E3D1A] via-[#0F2A0F] to-black text-white mb-0 text-sm relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-green-400 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-gradient-to-tl from-yellow-400 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-6xl font-bold text-white/5">PMN</div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
          {/* Logo and Description */}
          <div className="space-y-6">
            <img
              src="/logo/logo-pmn.png"
              alt="PMN Logo"
              className="w-20 h-auto hover:scale-105 transition-transform duration-300"
            />
            <p className="text-white/80 text-sm leading-relaxed">
              Ce projet a pour objectif de faciliter l'accès des artisans aux
              marchés publics en leur permettant de concevoir et produire divers
              mobiliers de bureau.
            </p>
            
            {/* Sunuy Artisan Button */}
            <a
              href="https://sunuyartisan.pmn.sn/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-[#B89C00] to-[#FFD700] text-black px-6 py-3 rounded-xl font-semibold hover:from-[#FFD700] hover:to-[#B89C00] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Globe className="w-5 h-5" />
              Je télécharge Suñuy Artisan
              <ExternalLink className="w-4 h-4" />
            </a>
            
            <div className="flex space-x-4 pt-4">
              <a
                href="https://web.facebook.com/profile.php?viewas=100000686899395&id=61559819632773"
                className="p-3 rounded-full bg-white/10 hover:bg-blue-600 text-white hover:text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.linkedin.com/company/projet-mobilier-national/?viewAsMember=trueclearn"
                className="p-3 rounded-full bg-white/10 hover:bg-blue-700 text-white hover:text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://x.com/MobilierProjet"
                className="p-3 rounded-full bg-white/10 hover:bg-black text-white hover:text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="w-5 h-5 fill-current"
                >
                  <g>
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                  </g>
                </svg>
              </a>
              <a
                href="https://www.instagram.com"
                className="p-3 rounded-full bg-white/10 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 text-white hover:text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.youtube.com/@projetmobiliernational"
                className="p-3 rounded-full bg-white/10 hover:bg-red-600 text-white hover:text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Youtube size={20} />
              </a>
              <a
                href="https://whatsapp.com/channel/0029Vb6Y00u35fM0RHslcQ3x"
                className="p-3 rounded-full bg-white/10 hover:bg-green-500 text-white hover:text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32 32"
                  className="w-5 h-5 fill-current"
                >
                  <path d="M16.003 2.5c-7.437 0-13.5 6.063-13.5 13.5 0 2.38.63 4.698 1.826 6.737L2.5 29.5l6.982-1.782A13.444 13.444 0 0 0 16.003 29c7.437 0 13.5-6.063 13.5-13.5s-6.063-13.5-13.5-13.5zm0 24.5c-2.013 0-3.981-.532-5.702-1.54l-.408-.24-4.14 1.057 1.104-4.025-.26-.416a11.47 11.47 0 0 1-1.737-6.076c0-6.327 5.147-11.5 11.5-11.5s11.5 5.173 11.5 11.5-5.147 11.5-11.5 11.5zm6.515-8.695c-.364-.182-2.165-1.072-2.501-1.194-.336-.121-.582-.182-.827.182s-.95 1.194-1.165 1.44c-.215.243-.43.273-.793.091-.364-.182-1.534-.566-2.921-1.807-1.08-.96-1.807-2.147-2.017-2.511-.21-.364-.023-.56.158-.742.162-.162.364-.424.546-.636.182-.212.242-.364.364-.606.121-.243.06-.455-.03-.636-.091-.182-.827-2.001-1.133-2.75-.298-.715-.601-.617-.827-.628l-.702-.013c-.243 0-.637.091-.97.455-.334.364-1.276 1.246-1.276 3.035s1.307 3.516 1.488 3.757c.182.243 2.567 3.926 6.228 5.506.871.375 1.55.598 2.08.765.873.278 1.667.239 2.294.145.7-.105 2.165-.886 2.472-1.744.303-.857.303-1.59.212-1.744-.09-.152-.333-.243-.697-.424z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Menu Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white border-b border-white/20 pb-2">Menu</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="/actualites"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Nos Actualités
                </a>
              </li>
              <li>
                <a
                  href="/cordonnateur"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Mot du Coordonnateur
                </a>
              </li>
              <li>
                <a
                  href="/organigramme"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  L'organigramme
                </a>
              </li>
              <li>
                <a
                  href="/commite-de-pilotage"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Le Comité de Pilotage (COPIL)
                </a>
              </li>
              <li>
                <a
                  href="/equipe"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Notre équipe du PMN
                </a>
              </li>
              <li>
                <a
                  href="https://pmn.vercel.app/marchespublics"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Portail des marchés publics du sénégal
                </a>
              </li>
              <li>
                <a
                  href="/#"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Fiche de recensement des partenaires
                </a>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white border-b border-white/20 pb-2">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="/realisations"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Réalisations
                </a>
              </li>
              <li>
                <a
                  href="/regulations"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Textes Réglementaires
                </a>
              </li>
              <li>
                <a
                  href="/four"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Problématique des 4F
                </a>
              </li>
              <li>
                <a
                  href="/filieres-artisanales"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Les filières artisanales
                </a>
              </li>
              <li>
                <a
                  href="/benificiaires-du-pmn"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Bénéficiaires du PMN
                </a>
              </li>
              <li>
                <a
                  href="/gallery"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  Notre Médiathèque
                </a>
              </li>
              <li>
                <a
                  href="https://sunuyartisan.pmn.sn/legal"
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="w-1 h-1 bg-[#B89C00] rounded-full group-hover:w-2 transition-all duration-300"></span>
                  CGU de l'Application "Suñuy Artisan"
                </a>
              </li>
            </ul>
          </div>
          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white border-b border-white/20 pb-2">Rejoignez-nous</h3>
            <ul className="space-y-4">
              <li className="flex items-center space-x-3 group">
                <div className="p-2 rounded-full bg-white/10 group-hover:bg-green-500 transition-all duration-300">
                  <Phone size={16} className="text-white" />
                </div>
                <span className="text-white/80 group-hover:text-white transition-colors">+221 76 624 85 05</span>
              </li>
              <li className="flex items-center space-x-3 group">
                <div className="p-2 rounded-full bg-white/10 group-hover:bg-green-500 transition-all duration-300">
                  <Phone size={16} className="text-white" />
                </div>
                <span className="text-white/80 group-hover:text-white transition-colors">+221 33 944 09 43</span>
              </li>
              <li className="flex items-center space-x-3 group">
                <div className="p-2 rounded-full bg-white/10 group-hover:bg-blue-500 transition-all duration-300">
                  <Mail size={16} className="text-white" />
                </div>
                <a
                  href="mailto:info@pmn.sn"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  info@pmn.sn 
                </a>
              </li>
              <li className="flex items-start space-x-3 group">
                <div className="p-2 rounded-full bg-white/10 group-hover:bg-red-500 transition-all duration-300 mt-1">
                  <MapPin size={16} className="text-white" />
                </div>
                <span className="text-white/80 group-hover:text-white transition-colors leading-relaxed">
                  Diamniadio cité Senegindia Villa 009-TYPE A
                </span>
              </li>
            </ul>
            
            <div className="mt-6">
              <a
                href="https://wa.me/221766248505"
                className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32 32"
                  className="w-5 h-5 fill-current"
                >
                  <path d="M16.003 2.5c-7.437 0-13.5 6.063-13.5 13.5 0 2.38.63 4.698 1.826 6.737L2.5 29.5l6.982-1.782A13.444 13.444 0 0 0 16.003 29c7.437 0 13.5-6.063 13.5-13.5s-6.063-13.5-13.5-13.5zm0 24.5c-2.013 0-3.981-.532-5.702-1.54l-.408-.24-4.14 1.057 1.104-4.025-.26-.416a11.47 11.47 0 0 1-1.737-6.076c0-6.327 5.147-11.5 11.5-11.5s11.5 5.173 11.5 11.5-5.147 11.5-11.5 11.5zm6.515-8.695c-.364-.182-2.165-1.072-2.501-1.194-.336-.121-.582-.182-.827.182s-.95 1.194-1.165 1.44c-.215.243-.43.273-.793.091-.364-.182-1.534-.566-2.921-1.807-1.08-.96-1.807-2.147-2.017-2.511-.21-.364-.023-.56.158-.742.162-.162.364-.424.546-.636.182-.212.242-.364.364-.606.121-.243.06-.455-.03-.636-.091-.182-.827-2.001-1.133-2.75-.298-.715-.601-.617-.827-.628l-.702-.013c-.243 0-.637.091-.97.455-.334.364-1.276 1.246-1.276 3.035s1.307 3.516 1.488 3.757c.182.243 2.567 3.926 6.228 5.506.871.375 1.55.598 2.08.765.873.278 1.667.239 2.294.145.7-.105 2.165-.886 2.472-1.744.303-.857.303-1.59.212-1.744-.09-.152-.333-.243-.697-.424z" />
                </svg>
                Contacter via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Section */}
      <div className="bg-black/30 backdrop-blur-sm border-t border-white/10 py-6 relative z-10">
        <div className="container mx-auto px-4 text-center text-sm text-white/90">
          © {currentYear} Ministère de la Culture, de l'Artisanat et du Tourisme | Projet du
          Mobilier National
        </div>
      </div>
    </footer>
  );
};

export default Footer;
